// InvoiceFormPage.tsx
import React, { useState, ChangeEvent, useContext, useMemo } from 'react';
import { Box, Container, Divider, Grid, Typography, styled } from '@mui/material';
import { convertServerMessageToClient, formatNumberWithDecimals } from '../../util';
import ConfirmButton from '../ConfirmButton';
import { useTranslation } from 'react-i18next';
import InvoiceForm from '../InvoiceForm';
import { useNavigate, useParams } from 'react-router-dom';
import { FormData, isEnterpriseSelectionRestricted } from '../../util/enum';
import { createInvoiceFormRecord } from '../../util/apiUtil';
import { GlobalStateContext } from '../../provider/GlobalStateProvider';
import { logoBanner } from '../../assets/images';
import { aesDecrypt } from '../../util/aes';
import ErrorPage from './ErrorPage';


const FormContainer = styled(Container)({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    width: '100vw',
    padding: '5px 5px 0px 5px',
    flex: 1,
});

const InnerBox = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'var(--white)',
    flexDirection: 'column',
    padding: '1rem',
    border: '1px solid white',
    width: '100%',
    overflow: 'auto',
    flex: 1
});

const InvoiceFormPage: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const { globalState, setGlobalState } = useContext(GlobalStateContext);
    const globalStateFormData = globalState?.globalStateFormData;
    const invoiceDetail = globalState?.invoiceDetail;
    const isDev = process.env.NODE_ENV === 'development';
    const isPageIdValid = aesDecrypt(id) === `${globalState?.receiptnumber}:${globalState?.tel}`;
    const [formData, setFormData] = useState<FormData>({
        receiptnumber: globalState?.receiptnumber || '',
        tel: globalState?.tel || '',
        alignment: isEnterpriseSelectionRestricted(invoiceDetail?.SHOPID, invoiceDetail?.SHOPNAME, invoiceDetail?.INVOICEDATE)
            ? 'personal' : globalStateFormData?.alignment || 'enterprise',
        TaxCompany: globalStateFormData?.TaxCompany || (isDev ? '測試用 TaxCompany' : ''),
        TaxPersonal: globalStateFormData?.TaxPersonal || (isDev ? '測試用 TaxPersonal' : ''),
        TaxNo: globalStateFormData?.TaxNo || (isDev ? '測試用 TaxNo' : ''),
        TaxEmail: globalStateFormData?.TaxEmail || (isDev ? 'TestingTaxEmail@scac.com.cn' : ''),
        TaxAmount: globalStateFormData?.TaxAmount || (isDev ? 1 : 0),
        TaxRemark: globalStateFormData?.TaxRemark || (isDev ? '測試用 TaxRemark' : ''),
        RECEIPTAMOUNT: globalStateFormData?.RECEIPTAMOUNT || invoiceDetail?.RECEIPTAMOUNT || '-',
        RECEIPTDATE: globalStateFormData?.RECEIPTDATE || invoiceDetail?.RECEIPTDATE || '-',
        MEMBERNAME: globalStateFormData?.MEMBERNAME || invoiceDetail?.MEMBERNAME || '-',
        SHOPNAME: globalStateFormData?.SHOPNAME || invoiceDetail?.SHOPNAME || '-',
        MEMBERCODE: globalStateFormData?.MEMBERCODE || invoiceDetail?.MEMBERCODE || '-',
        INVOICEDATE: globalStateFormData?.INVOICEDATE || invoiceDetail?.INVOICEDATE || undefined,
        SHOPID: globalStateFormData?.SHOPID || invoiceDetail?.SHOPID || undefined,
    });

    const [submitError, setSubmitError] = useState<string>('');
    const [missingFields, setMissingFields] = useState<string[]>([]);
    const navigate = useNavigate();

    let reduceSumOfTaxTotalAmount = invoiceDetail?.TAXINVOICE.reduce((total: any, item: any) => total + Number(item.TaxAmount), 0);
    let accTaxTotalAmount = reduceSumOfTaxTotalAmount < 0 ? 0 : reduceSumOfTaxTotalAmount;

    const handleConfirm = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setSubmitError(''); // Reset submitError when the user clicks the Confirm button

        // Check if required fields are provided
        if (!formData?.receiptnumber || !formData?.tel) {
            alert(t('error.invoiceOrPhoneNumberIncorrect'));
            navigate('/');
            return;
        }

        // Email validation (should be in correct format)
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
        if (!emailRegex.test(formData?.TaxEmail || '')) {
            alert(t('error.invalidEmailFormat'));
            setMissingFields(['TaxEmail']);
            return;
        }
        
        // Check if the enterprise selection is restricted
        if(isEnterpriseSelectionRestricted(invoiceDetail?.SHOPID, invoiceDetail?.SHOPNAME, invoiceDetail?.INVOICEDATE)) {
            if (formData?.alignment === 'enterprise') {
                alert(t('error.enterpriseNotAllowed'));
                return;
            }
        }

        // Convert TaxAmount and RECEIPTAMOUNT to numbers
        const TaxAmount = Number(formData?.TaxAmount);
        const RECEIPTAMOUNT = Number(formData?.RECEIPTAMOUNT);

        // Flag to control whether the checking of TaxAmount is enabled or not
        const isTaxAmountCheckEnabled = true;

        // Add tax amount check flag to check if tax amount is a number and not exceed max of total amount
        if (isTaxAmountCheckEnabled) {
            // TaxAmount validation (should be greater than 0)
            if (TaxAmount <= 0) {
                alert(t('error.taxAmountShouldBeGreaterThanZero'));
                return;
            }
            // Check if TaxAmount is a number
            if (!isNaN(TaxAmount)) {
                // Convert TaxAmount to a string and split it at the decimal point
                const [integerPart, fractionPart] = TaxAmount.toString().split('.');
                // Check if the fraction part has more than 2 digits
                if (fractionPart && fractionPart.length > 2) {
                    alert(t('error.taxAmountShouldMoreThanTwoFractionDigits'));
                    return;
                }
            }
            const roundedTaxAmount = +TaxAmount.toFixed(2);
            const roundedAccTaxTotalAmount = +accTaxTotalAmount.toFixed(2);
            const roundedRECEIPTAMOUNT = +RECEIPTAMOUNT.toFixed(2);

            if (roundedTaxAmount + roundedAccTaxTotalAmount > roundedRECEIPTAMOUNT) {
                alert(t('error.taxAmoutReachedLimit'));
                return;
            }

            // TaxAmount validation (not exceed max of total amount)
            if (TaxAmount + accTaxTotalAmount > RECEIPTAMOUNT) {
                alert(t('error.taxAmoutReachedLimit'));
                return;
            }
        }

        console.log('passing all conditions');
        const response = await createInvoiceFormRecord(formData, t);
        if (response?.missingFields || response?.error || response?.Result === 'false') {
            const error = response?.error || convertServerMessageToClient(response?.Message, t);
            setSubmitError(error);
            if (response?.Message === 'Tax amount exceeds') {
                alert(t('error.taxAmoutReachedLimit'));
            }
            setMissingFields(response?.missingFields || []);
            return;
        }
        else if (response?.Result === 'true') {
            setGlobalState(prevState => ({
                ...prevState,
                tel: '',
                receiptnumber: '',
            }));
            navigate('/invoiceForm/thankyou');
            return;
        }
        else {
            alert(t('error.default'));
            return;
        }
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [event.target.name]: event.target.value,
        });
    };
    const handleAlignment = (event: React.MouseEvent<HTMLElement>, newAlignment: 'enterprise' | 'personal') => {
        if (!newAlignment || newAlignment === formData.alignment) {
            return;
        }

        setFormData(prevState => ({
            ...prevState,
            alignment: newAlignment,
        }));
    };

    const errorMessage = submitError && (
        <Typography variant='body1' sx={{ color: 'var(--danger)', marginBottom: '1rem !important' }}>
            {submitError.split('\n').map((line, index) => (
                <span key={index}>
                    {line}
                    <br />
                </span>
            ))}
        </Typography>
    );

    const InfoBlock: React.FC<{ labelKey: string, value: string | undefined }> = ({ labelKey, value }) => {
        const { t } = useTranslation();

        return (
            <Typography variant="body2" gutterBottom sx={{ alignSelf: 'flex-end', marginRight: '0.5rem' }}>
                <span style={{ color: 'var(--secondary)', marginRight: '1rem' }}>
                    {t(labelKey)}
                </span>
                <span style={{ color: 'var(--dark)' }}>
                    {value}
                </span>
            </Typography>
        );
    }

    const createInfoBlocks = useMemo(() => {
        const infoBlocks = [
            { labelKey: 'invoiceForm.text.SHOPNAME', value: formData.SHOPNAME },
            { labelKey: 'invoiceForm.text.MEMBERCODE', value: formData.MEMBERCODE },
            { labelKey: 'invoiceForm.text.MEMBERNAME', value: formData.MEMBERNAME },
            { labelKey: 'invoiceForm.text.RECEIPTNUMBER', value: formData.receiptnumber },
            { labelKey: 'invoiceForm.text.RECEIPTDATE', value: formData.RECEIPTDATE },
            {
                labelKey: 'invoiceForm.text.accinvoiceTotalAmount',
                value: formatNumberWithDecimals({ number: accTaxTotalAmount, showCurrency: false })
            },
            {
                labelKey: 'invoiceForm.text.invoiceTotalAmount',
                value: formatNumberWithDecimals({ number: formData.RECEIPTAMOUNT, showCurrency: false })
            },
        ];

        return (
            <Grid container spacing={2} sx={{ width: '90%' }}>
                {infoBlocks.map(({ labelKey, value }) => (
                    <InfoBlock
                        key={labelKey}
                        labelKey={labelKey}
                        value={(!formData.receiptnumber || !formData.tel) ? '-' : value}
                    />
                ))}
            </Grid>
        );
    }, [accTaxTotalAmount, formData.MEMBERCODE, formData.MEMBERNAME, formData.RECEIPTAMOUNT, formData.RECEIPTDATE, formData.SHOPNAME, formData.receiptnumber, formData.tel])

    if (isPageIdValid) {
        return (
            <form noValidate autoComplete="off" style={{ flex: 1, display: 'flex' }}>
                <FormContainer>
                    <InnerBox>
                        <Box>
                            <img src={logoBanner} alt="logoBanner" />
                        </Box>
                        <Divider sx={{ width: '90%', borderWidth: '1px', borderColor: 'var(--mainBackground)', marginBottom: '1.5rem' }} />
                        {createInfoBlocks}
                        {/* splitLine */}
                        <Box sx={{
                            width: '90%',
                            height: '1px',
                            marginBottom: '1rem',
                            backgroundImage: 'repeating-linear-gradient(140deg, var(--mainBackground), var(--mainBackground) 2px, transparent 2px, transparent 4px)',
                        }}
                        />
                        <Container
                            sx={{
                                overflow: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: '1rem',
                                height: '100%',
                            }}
                        >
                            <InvoiceForm
                                formData={formData}
                                handleInputChange={handleInputChange}
                                alignment={formData.alignment}
                                handleAlignment={handleAlignment}
                                missingFields={missingFields}
                                invoiceDetail={invoiceDetail}
                            />
                            {errorMessage}
                        </Container>
                    </InnerBox>
                    <ConfirmButton
                        onClick={handleConfirm}
                        title={t('invoiceForm.button.text.submit')}
                        titleVariant='body1'
                        additionalStyle={{ marginTop: '0.2rem', marginBottom: '0.2rem' }}
                    />
                </FormContainer>
            </form>
        );
    }
    return <ErrorPage />;
}

export default InvoiceFormPage;